import React, { useState, useEffect } from 'react';
import Dropdown from 'react-bootstrap/Dropdown';
import { useTranslation } from 'react-i18next';
import usePrevious from 'use-previous';
import classNames from 'classnames';
import isEqual from 'fast-deep-equal';
import { AutosaveTextarea, Checkbox, DatePicker, Icon, Input } from '@fikasio/react-ui-components';
import AddTimeEntryModal from 'components/tasks/AddTimeEntryModal';
import CompletionTimeEntryModal from 'components/tasks/CompletionTimeEntryModal';
import { taskEstimatedRemainingMinutes } from 'components/tasks/TaskPane/timeLog';
import TrashIcon from 'components/UI/TrashIcon';
import ProjectTag from 'components/projects/ProjectTag';
import { projectStatus } from 'components/projects/utils';
import AssigneeButton from 'components/tasks/TasksViewer/AssigneeButton';
import ProjectSelectorButton from 'components/tasks/TasksViewer/ProjectSelectorButton';
import TaskImportanceDropdown from 'components/tasks/TasksViewer/TaskImportanceDropdown';
import TaskStatusDropdown from 'components/tasks/TasksViewer/TaskStatusDropdown';
import { ARCHIVED_STATUS, isNewlyCompleted, statusChangePatch, visibleStatus } from 'components/tasks/TasksViewer/statusChange';
import CollapseToggle from 'components/UI/CollapseToggle';
import DropdownToggle from 'components/UI/DropdownToggle';
import { useGetProjectsQuery } from 'services/projects/api';
import { useAddTaskMutation, usePatchTaskMutation, useDeleteTaskMutation } from 'services/tasks/api';
import { mutationFailed } from 'services/tasks/apiError';
import { Task, TaskImportance, isClosedTaskStatus, isOpenTaskStatus } from 'services/tasks/types';
import { round } from 'utils/maths';
import { formatPickerDate, isDueDatePast, pickedDateTimeInUserZone, toPickerDate } from 'utils/date';
import styles from './TasksList.module.css';

export interface TaskRowProps {
  autoFocus?: boolean;
  canCollapse?: boolean;
  depth?: number;
  isCollapsed?: boolean;
  isSelected?: boolean;
  onAddTask: (task: Task) => Promise<void>;
  onClick: (taskId: string) => Promise<void>;
  onDescriptionBlur?: () => void;
  onDescriptionFocus?: () => void;
  onTaskCompleted?: (task: Task) => void;
  onSelect: (task: Task, event?: { shiftKey?: boolean }) => void;
  onToggleCollapsed?: () => void;
  parent?: Task;
  previousSibling?: Task;
  projectId?: string;
  task: any;
  taskDefaults?: Partial<Task>;
}

const TaskRow = ({
  autoFocus,
  canCollapse = false,
  depth = 0,
  isCollapsed = false,
  isSelected,
  onAddTask,
  onClick,
  onDescriptionBlur,
  onDescriptionFocus,
  onSelect,
  onTaskCompleted,
  onToggleCollapsed,
  parent,
  previousSibling,
  projectId,
  task: pTask,
  taskDefaults,
}: TaskRowProps) => {
  const { t } = useTranslation();
  const { data: projects } = useGetProjectsQuery();

  const prevPTask = usePrevious(pTask);
  const [task, setTask] = useState(pTask);
  const [isDueAtDatepickerOpen, setIsDueAtDatepickerOpen] = useState(false);
  const [timeEntryMode, setTimeEntryMode] = useState<'add' | 'complete' | null>(null);

  const [createTask] = useAddTaskMutation();
  const [patchTask] = usePatchTaskMutation();
  const [deleteTask] = useDeleteTaskMutation();

  const saveChange = async (
    previous: typeof task,
    next: typeof task,
    request: Promise<unknown>,
  ) => {
    setTask(next);
    const result = await request;
    if (mutationFailed(result)) {
      setTask(previous);
      return false;
    }
    return true;
  };

  useEffect(() => {
    if (!isEqual(pTask, prevPTask)) {
      setTask({
        ...task,
        ...pTask,
      });
    }
  }, [pTask]);

  const handleKeyDownDescription = (e, updatedTask) => {
    if (e.key === 'Tab' && updatedTask.id) {
      const target = e.target as HTMLInputElement;
      if (target.selectionStart === 0 && target.selectionEnd === 0) {
        if (e.shiftKey && updatedTask.parentTaskId) {
          e.preventDefault();
          patchTask({
            id: updatedTask.id,
            parentTaskId: parent?.parentTaskId ?? null,
          });
        } else if (!e.shiftKey && previousSibling?.id) {
          e.preventDefault();
          patchTask({
            id: updatedTask.id,
            parentTaskId: previousSibling.id,
          });
        }
      }
      return;
    }
    if (e.key === 'Enter') {
      onAddTask({
        type: 'Template',
        description: '',
        dueAt: updatedTask.dueAt,
        ...(updatedTask.parentTaskId && { parentTaskId: updatedTask.parentTaskId }),
        ...(projectId && { projects: [{ id: projectId }] }),
        ...taskDefaults,
      });
      e.preventDefault();
    }
  };

  const handleKeyUpDescription = (e, updatedTask) => {
    const value = (e.target as HTMLInputElement).value;
    if (updatedTask) {
      if (e.key === 'Backspace' && value === '') {
        deleteTask(updatedTask.id);
      }
    } else if (value !== '') {
      createTask({
        description: value,
      });
    }
  };

  const handleTaskStatusChange = async (updatedTask, status, extras?: { cancelReason?: string | null }) => {
    const patch = statusChangePatch(status, {
      cancelReason: extras?.cancelReason,
      wasArchived: updatedTask.isArchived,
    });
    const next = status === ARCHIVED_STATUS
      ? {
        ...updatedTask,
        isArchived: true,
      }
      : {
        ...updatedTask,
        isArchived: false,
        status,
        ...(status === 'Cancelled' ? { cancelReason: extras?.cancelReason ?? null } : { cancelReason: null }),
      };
    const opensCompletion = isNewlyCompleted(updatedTask.status, status);
    if (opensCompletion) {
      if (onTaskCompleted) {
        onTaskCompleted(updatedTask);
      } else {
        setTimeEntryMode('complete');
      }
    }
    const saved = await saveChange(
      updatedTask,
      next,
      updatedTask.id
        ? patchTask({
          id: updatedTask.id,
          ...patch,
        })
        : createTask({
          ...updatedTask,
          type: 'Instance',
          ...patch,
        }),
    );
    if (!saved && opensCompletion && !onTaskCompleted) {
      setTimeEntryMode(null);
    }
  };

  const handleImportanceChange = (updatedTask, importance: TaskImportance | null) => {
    if (!updatedTask.id) {
      setTask({
        ...updatedTask,
        importance,
      });
      return;
    }
    saveChange(
      updatedTask,
      {
        ...updatedTask,
        importance,
      },
      patchTask({
        id: updatedTask.id,
        importance,
      }),
    );
  };

  const handleAssigneeChange = (updatedTask, assignee: string | null) => {
    if (!updatedTask.id) {
      setTask({
        ...updatedTask,
        assignee,
      });
      return;
    }
    saveChange(
      updatedTask,
      {
        ...updatedTask,
        assignee,
      },
      patchTask({
        id: updatedTask.id,
        assignee,
      }),
    );
  };

  const assignedProjectIds = new Set((task.projects || []).map(p => p.id));
  const availableProjects = (projects || [])
    .filter(p => !p.isArchived && projectStatus(p) !== 'Cancelled' && !assignedProjectIds.has(p.id))
    .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));

  const handleProjectSelect = (project: { id: string }) => {
    const updatedProjects = [...(task.projects || []), { id: project.id }];
    if (!task.id) {
      setTask({
        ...task,
        projects: updatedProjects,
      });
      return;
    }
    saveChange(
      task,
      {
        ...task,
        projects: updatedProjects,
      },
      patchTask({
        id: task.id,
        projects: updatedProjects,
      }),
    );
  };

  const dueAtDate = toPickerDate(task.dueAt);
  const estimateMinutes = taskEstimatedRemainingMinutes(task);
  const estimateLabel = typeof estimateMinutes === 'number'
    ? round(estimateMinutes / 60, 2).toString()
    : undefined;
  const hasdueAtPassed = task
    && isOpenTaskStatus(task.status)
    && isDueDatePast(task.dueAt);

  const handleDueAtChange = (dueAt: Date) => {
    const timestamp = pickedDateTimeInUserZone(dueAt)
      .set({ hour: 23, minute: 59, second: 59, millisecond: 999 });
    const nextDueAt = timestamp.toJSDate();
    if (!task.id) {
      setTask({
        ...task,
        dueAt: nextDueAt,
      });
      return;
    }
    saveChange(
      task,
      {
        ...task,
        dueAt: nextDueAt,
      },
      patchTask({
        id: task.id,
        dueAt: nextDueAt,
      }),
    );
  };

  const handleDueAtRemove = (e: React.SyntheticEvent) => {
    e.stopPropagation();
    if (!task.id) {
      setTask({
        ...task,
        dueAt: null,
      });
      return;
    }
    saveChange(
      task,
      {
        ...task,
        dueAt: null,
      },
      patchTask({
        id: task.id,
        dueAt: null,
      }),
    );
  };

  return (
    <tr
      className={classNames({
        [styles.taskRow]: true,
        done: task && isClosedTaskStatus(task.status),
        selected: isSelected,
      })}
    >
      <td
        onClick={e => {
          e.stopPropagation();
          onSelect(task, e);
        }}
        onMouseDown={e => {
          if (e.shiftKey) {
            e.preventDefault();
          }
        }}
        style={{
          textAlign: 'center',
        }}
        width={35}
      >
        <Checkbox isChecked={isSelected} />
      </td>
      <td
        className={styles.taskRowStatus}
        width={35}
      >
        <TaskStatusDropdown
          onChange={(status, extras) => handleTaskStatusChange(task, status, extras)}
          status={visibleStatus(task.status, task.isArchived)}
        />
      </td>
      <td
        onClick={e => {
          if (e.shiftKey) {
            e.preventDefault();
            onSelect(task, e);
            return;
          }
          onClick?.(task.id);
        }}
        onMouseDown={e => {
          if (e.shiftKey) {
            e.preventDefault();
          }
        }}
        style={{
          cursor: 'pointer',
          paddingLeft: 8 + (depth * 24),
        }}
      >
        <div className={styles.taskRowDescriptionContent}>
          {
            canCollapse && (
              <CollapseToggle
                isCollapsed={isCollapsed}
                onToggle={() => onToggleCollapsed?.()}
              />
            )
          }
          <Input
            autoFocus={autoFocus}
            className={classNames({
              [styles.taskRowDescriptionEditable]: true,
              [task.id]: true,
            })}
            defaultValue={task.description || ''}
            onBlur={() => onDescriptionBlur?.()}
            onFocus={() => onDescriptionFocus?.()}
            onKeyDown={e => handleKeyDownDescription(e, task)}
            onKeyUp={e => handleKeyUpDescription(e, task)}
            onSave={value => {
              if (task.id) {
                patchTask({
                  id: task.id,
                  description: value,
                });
              } else {
                createTask({
                  ...task,
                  description: value,
                })
                  .then(({ data: resultTask }) => {
                    if (resultTask) {
                      setTask(resultTask);
                    }
                  });
              }
            }}
            style={{
              flex: '1 1 auto',
              height: 25,
              minWidth: 0,
              overflowY: 'hidden',
              paddingLeft: 5,
              paddingRight: 50,
              paddingTop: 0,
              width: 'auto',
            }}
          />
        </div>
      </td>
      <td
        className={styles.taskRowProjects}
        width={160}
      >
        {
          task && task.projects && task.projects
            .map(p => (
              <ProjectTag
                key={p.id}
                projectId={p.id}
              />
            ))
        }
        <ProjectSelectorButton
          onSelect={handleProjectSelect}
          projects={availableProjects}
        />
      </td>
      <td
        style={{
          cursor: 'pointer',
          width: 80,
        }}
      >
        <Icon
          name="stopwatch"
          size="1x"
          style={{ marginRight: 10 }}
        />
        <AutosaveTextarea
          defaultValue={estimateLabel}
          key={estimateLabel ?? 'none'}
          onSave={async value => {
            patchTask({
              id: task.id,
              estimatedCompletionTime: Math.round(parseFloat(value) * 60),
            });
          }}
          style={{
            display: 'inline-block',
            height: 25,
            minWidth: 25,
            overflowY: 'hidden',
            padding: 0,
            width: 'auto',
          }}
          useContentEditableDiv
        />
      </td>
      <td
        className={classNames('taskRow_dueAt', styles.taskRowDueAt)}
        onClick={() => setIsDueAtDatepickerOpen(true)}
        style={{
          cursor: 'pointer',
          whiteSpace: 'nowrap',
          ...(hasdueAtPassed && { color: '#ff0000' }),
          width: 110,
        }}
      >
        <DatePicker
          defaultValue={dueAtDate}
          displayFunction={formatPickerDate}
          isOpen={isDueAtDatepickerOpen}
          key={dueAtDate ? 'has-due-at' : 'no-due-at'}
          onChange={handleDueAtChange}
          onClose={() => setIsDueAtDatepickerOpen(false)}
          onRemoveValue={handleDueAtRemove}
          shouldCloseOnSelect
          showRemoveValue
          showTimeSelect={false}
          value={dueAtDate}
        />
      </td>
      <td
        className={styles.taskRowAssignee}
        width={35}
      >
        <AssigneeButton
          assigneeId={task.assignee}
          onChange={assignee => handleAssigneeChange(task, assignee)}
        />
      </td>
      <td
        className={styles.taskRowStatus}
        width={35}
      >
        <TaskImportanceDropdown
          importance={task.importance}
          onChange={importance => handleImportanceChange(task, importance)}
        />
      </td>
      <td
        className={styles.taskRowActions}
        width={30}
      >
        <Dropdown>
          <Dropdown.Toggle as={DropdownToggle} />
          <Dropdown.Menu
            data-testid="task-actions-menu"
            popperConfig={{
              modifiers: [{ name: 'hide', enabled: false }],
            }}
          >
            {
              task.id && task.type !== 'Instance' && (
                <Dropdown.Item
                  onClick={() => onAddTask({
                    type: 'Template',
                    description: '',
                    parentTaskId: task.id,
                    dueAt: task.dueAt,
                    ...(projectId && { projects: [{ id: projectId }] }),
                    ...taskDefaults,
                  })}
                >
                  <Icon
                    name="plus"
                    style={{
                      marginRight: 10,
                      width: 25,
                    }}
                  />
                  {t('addSubtask')}
                </Dropdown.Item>
              )
            }
            {
              task.id && (
                <Dropdown.Item
                  onClick={() => setTimeEntryMode('add')}
                >
                  <Icon
                    name="stopwatch"
                    style={{
                      marginRight: 10,
                      width: 25,
                    }}
                  />
                  {t('addTimeEntry')}
                </Dropdown.Item>
              )
            }
            <Dropdown.Item
              onClick={() => createTask(task)}
            >
              <Icon
                name="copy"
                style={{
                  marginRight: 10,
                  width: 25,
                }}
              />
              {t('duplicate')}
            </Dropdown.Item>
            <Dropdown.Item
              onClick={() => deleteTask(task.id)}
            >
              <TrashIcon
                style={{
                  color: 'red',
                  marginRight: 10,
                  width: 25,
                }}
              />
              {t('delete')}
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
        {
          timeEntryMode === 'add' && task.id && (
            <AddTimeEntryModal
              onHide={() => setTimeEntryMode(null)}
              onSave={patch => {
                setTask({
                  ...task,
                  ...patch,
                });
                patchTask({
                  id: task.id,
                  ...patch,
                });
              }}
              show
              task={task}
            />
          )
        }
        {
          timeEntryMode === 'complete' && task.id && (
            <CompletionTimeEntryModal
              onHide={() => setTimeEntryMode(null)}
              onSave={patch => {
                setTask({
                  ...task,
                  ...patch,
                });
                patchTask({
                  id: task.id,
                  ...patch,
                });
              }}
              task={task}
            />
          )
        }
      </td>
    </tr>
  );
};

export default TaskRow;
